import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import i18n, { toUiLocale } from '@/i18n/config';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TenantProvider, useTenant } from '@/contexts/TenantContext';
import { ConfirmProvider } from '@/contexts/ConfirmProvider';
import { APIProvider as GoogleMapsProvider } from '@vis.gl/react-google-maps';
import { logout as apiLogout } from '@/utils/api';
import { installGlobalAxiosInterceptors } from '@/lib/apiClient';
import { registerServiceWorker } from '@/lib/pushClient';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import ChatAssistant from '@/components/ChatAssistant';
import { Eye, EyeOff, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnyModalOpen } from '@/hooks/useAnyModalOpen';
import { SpinningBrainLoader } from '@/components/SpinningLogo';
import { lazyWithRetry as lazy } from '@/utils/lazyWithRetry';

// Chat Widget Toggle Component - hides/shows the Emergent chat bubble
const ChatWidgetToggle = () => {
  const { t } = useTranslation();
  const modalOpen = useAnyModalOpen();
  const [chatVisible, setChatVisible] = useState(() => {
    return localStorage.getItem('turbuss_chat_visible') !== 'false';
  });

  useEffect(() => {
    // Save preference
    localStorage.setItem('turbuss_chat_visible', chatVisible ? 'true' : 'false');
    
    // Toggle body class for CSS-based hiding
    if (chatVisible) {
      document.body.classList.remove('emergent-chat-hidden');
    } else {
      document.body.classList.add('emergent-chat-hidden');
    }
    
    // Find and manipulate chat widgets directly
    const hideShowWidgets = () => {
      // Find elements that look like chat widgets
      document.querySelectorAll('div, button, iframe').forEach((el: any) => {
        if (el.id === 'chat-toggle-react' || el.closest('#root')) return;
        // Skip Radix/shadcn dialogs, overlays and popovers (portalled outside #root)
        if (el.getAttribute && (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog')) return;
        if (el.closest && (el.closest('[role="dialog"]') || el.closest('[role="alertdialog"]') || el.closest('[data-radix-portal]') || el.closest('[data-radix-popper-content-wrapper]') || el.closest('[data-sonner-toaster]'))) return;
        const htmlEl = el as HTMLElement;
        
        const style = window.getComputedStyle(htmlEl);
        const zIndex = parseInt(style.zIndex) || 0;
        const pos = style.position;
        const bottom = parseInt(style.bottom);
        const right = parseInt(style.right);
        
        // Chat widgets: fixed position, bottom-right corner, very high z-index
        if (pos === 'fixed' && zIndex > 9999 && !isNaN(bottom) && bottom < 100 && !isNaN(right) && right < 100) {
          // Amendment A: also force-hide the 3rd-party bubble while a modal is open
          // so it never overlaps the modal footer.
          if (chatVisible && !modalOpen) {
            htmlEl.style.removeProperty('display');
            htmlEl.style.removeProperty('visibility');
            htmlEl.style.removeProperty('opacity');
          } else {
            htmlEl.style.setProperty('display', 'none', 'important');
            htmlEl.style.setProperty('visibility', 'hidden', 'important');
            htmlEl.style.setProperty('opacity', '0', 'important');
          }
        }
      });
    };
    
    // Run immediately and periodically
    hideShowWidgets();
    const interval = setInterval(hideShowWidgets, 1000);
    
    return () => clearInterval(interval);
  }, [chatVisible, modalOpen]);

  // Amendment A: hide our toggle FAB while a modal with a footer is open.
  if (modalOpen) return null;
  if (window.location.pathname.startsWith('/demo') || window.location.pathname.startsWith('/katalog/') || window.location.pathname.startsWith('/kp/') || window.location.pathname.startsWith('/pl/') || window.location.pathname.startsWith('/sp/')) return null;

  return (
    <button
      id="chat-toggle-react"
      onClick={() => setChatVisible(!chatVisible)}
      className={`fixed bottom-5 right-24 z-[2147483646] px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex items-center gap-1.5 ${
        chatVisible 
          ? 'bg-muted/80 backdrop-blur text-muted-foreground border border-border/50 hover:bg-muted hover:text-foreground' 
          : 'bg-muted/80 backdrop-blur text-muted-foreground border border-border/50 hover:bg-muted hover:text-foreground'
      }`}
      title={chatVisible ? t('chat.hideChat', 'Click to hide chat bubble') : t('chat.showChat', 'Click to show chat bubble')}
    >
      {chatVisible ? (
        <>
          <EyeOff className="w-4 h-4" />
          {t('chat.hide', 'Skjul Chat')}
        </>
      ) : (
        <>
          <MessageCircle className="w-4 h-4" />
          {t('chat.show', 'Vis Chat')}
        </>
      )}
    </button>
  );
};

// Pages - Lazy loaded for code splitting
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import OfferAcceptPage from '@/pages/OfferAcceptPage';
import PublicTrackingPage from '@/pages/PublicTrackingPage';
import PublicItineraryPage from '@/pages/PublicItineraryPage';
import PublicDashboardPage from '@/pages/PublicDashboardPage';
import PublicFeedbackPage from '@/pages/PublicFeedbackPage';
import PublicAlarmPage from '@/pages/PublicAlarmPage';
import PublicOpenTripPage from '@/pages/PublicOpenTripPage';
import PublicCustomerPlanPage from '@/pages/PublicCustomerPlanPage';
import PublicDriverManifestPage from '@/pages/PublicDriverManifestPage';
import PublicDriverPlanPage from '@/pages/PublicDriverPlanPage';
import PublicOpenTripsCatalogPage from '@/pages/PublicOpenTripsCatalogPage';
const DemoCatalogPage = lazy(() => import('@/pages/DemoCatalogPage'));
const PublicTurkatalogPage = lazy(() => import('@/pages/PublicTurkatalogPage'));
import PublicOpenTripCancelPage from '@/pages/PublicOpenTripCancelPage';
import RegisterPage from '@/pages/RegisterPage';
import RequestAccessPage from '@/pages/RequestAccessPage';

const BookingMedarbeiderDashboard = lazy(() => import('@/pages/BookingMedarbeiderDashboard'));
const EnkeltpersonDashboard = lazy(() => import('@/pages/EnkeltpersonDashboard'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const DriverDashboard = lazy(() => import('@/pages/DriverDashboard'));
const ExternalDashboard = lazy(() => import('@/pages/ExternalDashboard'));
const SuperadminDashboard = lazy(() => import('@/pages/SuperadminDashboard'));

// Website / Landing pages - Lazy loaded
const WebsiteLayout = lazy(() => import('@/pages/website/WebsiteLayout'));
const HomePage = lazy(() => import('@/pages/website/HomePage'));
const FeaturesPage = lazy(() => import('@/pages/website/FeaturesPage'));
const AboutPage = lazy(() => import('@/pages/website/AboutPage'));
const PricingPage = lazy(() => import('@/pages/website/PricingPage'));
const ContactPage = lazy(() => import('@/pages/website/ContactPage'));
const PrivacyPage = lazy(() => import('@/pages/website/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/website/TermsPage'));
const TakkPage = lazy(() => import('@/pages/website/TakkPage'));
const NotFoundPage = lazy(() => import('@/pages/website/NotFoundPage'));

// Suspense fallback
const PageLoader = () => <SpinningBrainLoader />;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth: Use Bearer token in localStorage (works cross-origin without CORS credentials).
// HttpOnly cookies still work as fallback when same-origin (preview), but we don't
// rely on `withCredentials` globally because Emergent's ingress returns
// `Access-Control-Allow-Origin: *` which is incompatible with credentials mode.
axios.defaults.withCredentials = false;
// P2: install shared request/response interceptors on the global axios instance so
// every existing direct `axios` call gets unified auth-token injection + error
// normalization (see src/lib/apiClient.ts). New code should import `api` instead.
installGlobalAxiosInterceptors();
const _storedToken = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
if (_storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_storedToken}`;
}
// Pre-rendret markedsside (#prerender, injisert i build) skal kun stå til React tar over på
// /website-ruter for anonyme; for app-ruter og innloggede fjernes laget umiddelbart.
if (typeof document !== 'undefined') {
  const _p = window.location.pathname.replace(/\/+$/, '') || '/';
  if (_storedToken || (_p !== '/' && !_p.startsWith('/website'))) document.getElementById('prerender')?.remove();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: true } } });

function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<any>(null);
  const [endingImpersonation, setEndingImpersonation] = useState(false);

  useEffect(() => {
    // Check authentication on app load via Bearer token (localStorage)
    fetchCurrentUser();

    // Phase 4 — register the PWA service worker (network-only APIs, kill-switch,
    // offline fallback only for true navigation-offline). Safe for all roles.
    registerServiceWorker();
    
    // Check for stored impersonation state
    const storedImpersonation = localStorage.getItem('impersonation');
    if (storedImpersonation) {
      try {
        setImpersonation(JSON.parse(storedImpersonation));
      } catch (e: any) {
        localStorage.removeItem('impersonation');
      }
    }
  }, []);

  // Keep global user in sync when the profile page updates it.
  useEffect(() => {
    const onUserUpdated = (e: Event) => {
      const updated = (e as CustomEvent).detail;
      if (updated) setUser((prev: any) => ({ ...prev, ...updated }));
    };
    window.addEventListener('user-updated', onUserUpdated);
    return () => window.removeEventListener('user-updated', onUserUpdated);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Set user's preferred language - prioritize user's saved preference in localStorage,
      // then backend preference, then default to Norwegian
      const localStorageLang = localStorage.getItem('user_preferred_language');
      const backendLang = toUiLocale(response.data.preferred_language);
      
      if (localStorageLang && (localStorageLang === 'en' || localStorageLang === 'no')) {
        // User has explicitly set language in browser - respect it
        i18n.changeLanguage(localStorageLang);
      } else if (backendLang) {
        // Use backend preference
        i18n.changeLanguage(backendLang);
      }
      // Otherwise keep current i18n language (set during init)
    } catch (error: any) {
      console.error('Error fetching user:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('user');
      } else {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            localStorage.removeItem('user');
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token: any, userData: any) => {
    // Store token in localStorage and set Authorization header for cross-origin
    // requests (production custom domain → backend deployment).
    if (token) {
      localStorage.setItem('access_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    // Clear any cached data from a previous identity so a fresh login never
    // shows stale (e.g. previously-impersonated) customer-scoped data.
    queryClient.clear();

    // Trigger tenant config fetch
    window.dispatchEvent(new Event('tenant-refresh'));
    
    // Iter 761: kundekontakt → portalens standardspråk følger kontakt/kunde ved HVER innlogging
    // (manuell veksler etterpå gjelder kun økten/enheten). Ansatte: lokal preferanse først.
    const localStorageLang = localStorage.getItem('user_preferred_language');
    const backendLang = toUiLocale(userData.preferred_language);
    if ((userData.role === 'bedrift' || userData.is_contact_person) && backendLang) {
      i18n.changeLanguage(backendLang);
    } else if (localStorageLang && (localStorageLang === 'en' || localStorageLang === 'no')) {
      i18n.changeLanguage(localStorageLang);
    } else if (backendLang) {
      i18n.changeLanguage(backendLang);
    }
    // Otherwise keep current language
  };

  const handleLogout = async () => {
    // Use API logout to clear HttpOnly cookie (best-effort)
    try { await apiLogout(); } catch { /* ignore */ }
    // Clear Bearer token + cached state
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setImpersonation(null);
    localStorage.removeItem('impersonation');
    localStorage.removeItem('user');
    // Drop every cached query so the next user never inherits this session's data.
    queryClient.clear();
    // Don't reset language on logout - keep user's preference
  };

  // Handle starting impersonation
  const handleStartImpersonation = (impersonationData: any) => {
    if (impersonationData.success && impersonationData.user) {
      // Store impersonation data
      setImpersonation(impersonationData.impersonation);
      localStorage.setItem('impersonation', JSON.stringify(impersonationData.impersonation));

      // Purge cached admin data so the customer view starts clean.
      queryClient.clear();

      // Update token (new token issued for impersonated user)
      if (impersonationData.access_token) {
        localStorage.setItem('access_token', impersonationData.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${impersonationData.access_token}`;
      }
      setUser(impersonationData.user);
    }
  };

  // Handle ending impersonation
  const handleEndImpersonation = async () => {
    if (!impersonation?.original_user?.id) return;
    
    setEndingImpersonation(true);
    try {
      const response = await axios.post(`${API}/admin/end-impersonation`, {
        original_user_id: impersonation.original_user.id
      });
      
      if (response.data.success) {
        // Clear impersonation state
        setImpersonation(null);
        localStorage.removeItem('impersonation');

        // Drop all customer-scoped cached data immediately so admin views are
        // no longer scoped to that customer (previously required a full reload).
        queryClient.clear();

        // Restore original user's token
        if (response.data.access_token) {
          localStorage.setItem('access_token', response.data.access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        }
        setUser(response.data.user);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      console.error('End impersonation failed:', error?.response?.data?.detail || error);
      if (status === 401 || status === 403) {
        // Impersoneringsøkten er ugyldig/utløpt — tokenet kan ikke brukes til noe.
        // Ikke la brukeren sitte fast: rydd lokal tilstand og send til innlogging.
        toast.error(t('app.kunneIkkeBytteTilbakeOkten', 'Kunne ikke bytte tilbake – økten er utløpt. Du logges ut, logg inn på nytt.'));
        await handleLogout();
        return;
      }
      // Midlertidig feil (nett/server): behold økten så brukeren kan prøve igjen.
      toast.error(t('app.kunneIkkeBytteTilbakeProv', 'Kunne ikke bytte tilbake. Prøv igjen.'));
    } finally {
      setEndingImpersonation(false);
    }
  };

  if (loading) {
    return <SpinningBrainLoader />;
  }

  // Remount the whole app subtree whenever the acting identity changes (login,
  // logout, start/stop impersonation) so components that fetch on mount with
  // axios+useState re-fetch fresh, correctly-scoped data instead of showing a
  // previous identity's cached view until a manual page reload.
  const identityKey = `${user?.id || 'anon'}::${impersonation?.is_impersonating ? `cust-${impersonation?.customer?.id || 'x'}` : 'self'}`;

  return (
    <QueryClientProvider client={queryClient}>
    <GoogleMapsProvider apiKey={process.env.REACT_APP_GOOGLE_PLACES_API_KEY || ''} libraries={['places', 'geometry', 'routes']}>
    <ThemeProvider>
    <TenantProvider>
      <I18nextProvider i18n={i18n}>
      <ConfirmProvider>
        <div key={identityKey} className={`App ${impersonation?.is_impersonating ? 'pt-12' : ''}`}>
          {/* Skip to content - WCAG compliance */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
            data-testid="skip-to-content"
          >
            {t('app.hoppTilHovedinnhold', 'Hopp til hovedinnhold')}
          </a>
          {/* Impersonation Banner */}
          <ImpersonationBanner
            impersonationData={impersonation}
            onEndImpersonation={handleEndImpersonation}
            onLogout={handleLogout}
            isLoading={endingImpersonation}
          />
          
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public website / landing pages */}
              <Route path="/website" element={<WebsiteLayout />}>
                <Route index element={<HomePage />} />
                <Route path="features" element={<FeaturesPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="takk" element={<TakkPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              <Route path="/login" element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to="/" />} />
              <Route path="/login/:slug" element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to="/" />} />
              <Route path="/register" element={!user ? <RegisterPage onLogin={handleLogin} /> : <Navigate to="/" />} />
              <Route path="/request-access" element={!user ? <RequestAccessPage /> : <Navigate to="/" />} />
              <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/tilbud/godta/:token" element={<OfferAcceptPage />} />
              {/* Phase 2 — public customer-facing pages (no auth) */}
              <Route path="/t/:token" element={<PublicTrackingPage />} />
              <Route path="/d/:token" element={<PublicDashboardPage />} />
              {/* Phase 3 — public post-trip feedback page (no auth) */}
              <Route path="/f/:token" element={<PublicFeedbackPage />} />
              {/* Phase 4 — public driver-alarm JA/NEI response page (no auth) */}
              <Route path="/a/:token" element={<PublicAlarmPage />} />
              {/* Fase K3 — public customer itinerary / reiseplan (no auth) */}
              <Route path="/r/:token" element={<PublicItineraryPage />} />
              {/* Fase K4 — public open-trip signup + self-cancel (no auth) */}
              <Route path="/p/:token" element={<PublicOpenTripPage />} />
              {/* Kundens bussplan-lenke — offentlig, uten innlogging, speiler Ukesplan (kun egne turer, publiserte datoer) */}
              <Route path="/kp/:token" element={<PublicCustomerPlanPage />} />
              {/* Iter 798: sjåførens passasjerliste per tur — offentlig, uten innlogging, kun én tur (publisert til sjåfør/eksterne) */}
              <Route path="/pl/:token" element={<PublicDriverManifestPage />} />
              {/* Iter 801: sjåførens/eksternt selskaps hel-plan-lenke — alle egne turer, historikk + publiserte datoer, bla i perioder */}
              <Route path="/sp/:token" element={<PublicDriverPlanPage />} />
              <Route path="/o/:token" element={<PublicOpenTripsCatalogPage />} />
              {/* Turkatalog 2027 — offentlig, delbar kundekatalog (ingen innlogging) */}
              <Route path="/katalog/:token" element={<PublicTurkatalogPage />} />
              {/* Intern forhåndsvisning av turkatalogen (innlogget ansatt, uavhengig av publisering) */}
              <Route path="/katalog/forhandsvisning/:catalogId" element={<PublicTurkatalogPage preview />} />
              <Route path="/avmeld/:token" element={<PublicOpenTripCancelPage />} />
              {/* Gatet demo-katalog (tilgangskode, ingen app-innlogging) */}
              <Route path="/demo" element={<DemoCatalogPage />} />
              
              <Route path="/" element={
                user ? (
                  user.role === 'superadmin' ? <SuperadminDashboard user={user} onLogout={handleLogout} /> :
                  user.role === 'bedrift' ? <BookingMedarbeiderDashboard user={user} onLogout={handleLogout} onStartImpersonation={handleStartImpersonation} /> :
                  user.role === 'customer' ? <BookingMedarbeiderDashboard user={user} onLogout={handleLogout} onStartImpersonation={handleStartImpersonation} /> :
                  user.role === 'enkeltperson' ? <EnkeltpersonDashboard user={user} onLogout={handleLogout} /> :
                  user.role === 'admin' ? <BookingMedarbeiderDashboard user={user} onLogout={handleLogout} onStartImpersonation={handleStartImpersonation} /> :
                  user.role === 'sjåfør' ? <DriverDashboard user={user} onLogout={handleLogout} /> :
                  user.role === 'driver' ? <DriverDashboard user={user} onLogout={handleLogout} /> :
                  user.role === 'booking_medarbeider' ? <BookingMedarbeiderDashboard user={user} onLogout={handleLogout} onStartImpersonation={handleStartImpersonation} /> :
                  user.role === 'external' ? <ExternalDashboard user={user} onLogout={handleLogout} /> :
                  user.role === 'ekstern_operatør' ? <ExternalDashboard user={user} onLogout={handleLogout} /> :
                  <Navigate to="/website" />
                ) : <Navigate to="/website" />
              } />
              {/* Dedicated URL for "Ny enkeltbestilling" — gives the form its own
                  shareable/bookmarkable route. The dashboard auto-opens the booking
                  view based on this pathname. Issue 1 (page vs modal). */}
              <Route path="/bookings/new" element={
                user ? (
                  ['admin','booking_medarbeider','bedrift','customer'].includes(user.role)
                    ? <BookingMedarbeiderDashboard user={user} onLogout={handleLogout} onStartImpersonation={handleStartImpersonation} />
                    : <Navigate to="/" />
                ) : <Navigate to="/website" />
              } />
              <Route path="*" element={user ? <Navigate to="/" replace /> : <NotFoundPage />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" />
          <ChatWidgetToggle />
          {user && !window.location.pathname.startsWith('/katalog/') && !window.location.pathname.startsWith('/kp/') && !window.location.pathname.startsWith('/pl/') && !window.location.pathname.startsWith('/sp/') && !window.location.pathname.startsWith('/demo') && <ChatAssistant user={user} />}
        </div>
      </ConfirmProvider>
      </I18nextProvider>
    </TenantProvider>
    </ThemeProvider>
    </GoogleMapsProvider>
    </QueryClientProvider>
  );
}

export default App;
